import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { fetchCustomReportShares, saveCustomReportShares } from '../../api/customReportsApi'
import { listUserOptions, listRoleOptions } from '../../../masters/users/api/userApi'
import AppDropdown from '../../../../components/AppDropdown'
import AppToggle from '../../../../components/AppToggle'

const SHARE_TYPE_OPTIONS = [
  { value: 'ORG', label: 'Organization-Wide (All Employees)' },
  { value: 'DEPARTMENT', label: 'My Department' },
  { value: 'TEAM', label: 'My Project Team' },
  { value: 'ROLE', label: 'Specific Role' },
  { value: 'USER', label: 'Specific User' },
]

/**
 * SearchSelect - Autocomplete search input with a floating portal dropdown
 * Supports instant local filtering + debounced async API searching
 */
function SearchSelect({
  value,
  selectedName,
  onChange,
  onSearchAsync,
  initialOptions = [],
  placeholder = 'Type to search...',
  emptyMessage = 'No matches found',
  icon = 'search',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState(selectedName || '')
  const [asyncResults, setAsyncResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const panelRef = useRef(null)
  const [panelCoords, setPanelCoords] = useState({ top: 0, left: 0, width: 0 })

  // Sync external name changes
  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedName || '')
    }
  }, [isOpen, selectedName])

  // Compute position for floating portal dropdown
  const updateCoords = useCallback(() => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const dropdownHeight = 220
    const openUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight

    setPanelCoords({
      top: openUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: Math.max(8, rect.left),
      width: Math.max(rect.width, 260),
      maxHeight: dropdownHeight,
    })
  }, [])

  // Manage click outside and resize/scroll positioning
  useEffect(() => {
    if (!isOpen) return
    updateCoords()

    function onDocClick(e) {
      if (
        containerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return
      }
      setIsOpen(false)
      // If user typed without selecting, restore previous selection name
      setQuery(selectedName || '')
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setQuery(selectedName || '')
      }
    }

    document.addEventListener('mousedown', onDocClick)
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [isOpen, updateCoords, selectedName])

  // Debounced async search if provided
  useEffect(() => {
    if (!isOpen || !onSearchAsync) return
    const trimmed = query.trim()
    if (!trimmed || trimmed === selectedName) {
      setAsyncResults([])
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await onSearchAsync(trimmed)
        setAsyncResults(results || [])
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        setIsSearching(false)
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [query, isOpen, onSearchAsync, selectedName])

  // Combine initialOptions and asyncResults (avoid duplicates)
  const combinedOptions = useMemo(() => {
    const map = new Map()
    initialOptions.forEach((opt) => map.set(String(opt.id), opt))
    asyncResults.forEach((opt) => map.set(String(opt.id), opt))
    return Array.from(map.values())
  }, [initialOptions, asyncResults])

  // Filter options based on query
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q || q === (selectedName || '').toLowerCase()) {
      return combinedOptions
    }
    return combinedOptions.filter((opt) => {
      const nameMatch = opt.name?.toLowerCase().includes(q)
      const subMatch = opt.subText?.toLowerCase().includes(q)
      const codeMatch = opt.code?.toLowerCase().includes(q)
      return nameMatch || subMatch || codeMatch
    })
  }, [combinedOptions, query, selectedName])

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <div className="relative">
        <span className="material-symbols-outlined text-base text-on-surface-variant absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {icon}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => {
            setIsOpen(true)
            updateCoords()
          }}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
            updateCoords()
          }}
          placeholder={placeholder}
          className={`w-full rounded-xl border bg-surface-container-lowest px-3 py-2.5 pl-9 pr-9 text-sm text-on-surface shadow-sm transition hover:border-primary/35 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 ${
            !value ? 'border-outline-variant/40' : 'border-outline-variant/30'
          }`}
        />
        {query ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setQuery('')
              onChange(null, '')
              setIsOpen(true)
              inputRef.current?.focus()
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-on-surface-variant hover:text-on-surface"
            title="Clear selection"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        ) : (
          <span className="material-symbols-outlined text-base text-on-surface-variant absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            expand_more
          </span>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: `${panelCoords.top}px`,
              left: `${panelCoords.left}px`,
              width: `${panelCoords.width}px`,
              maxHeight: `${panelCoords.maxHeight}px`,
              zIndex: 9999,
            }}
            className="overflow-y-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest py-1.5 text-sm shadow-[0_14px_32px_rgba(15,23,42,0.2)]"
          >
            {isSearching ? (
              <div className="flex items-center justify-center gap-1.5 px-4 py-3 text-[11px] text-on-surface-variant">
                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                <span>Searching...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-[11px] text-on-surface-variant">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.id) === String(value)
                const initialLetter = (opt.name || '?').charAt(0).toUpperCase()
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      onChange(opt.id, opt.name)
                      setQuery(opt.name)
                      setIsOpen(false)
                    }}
                    className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                      isSelected
                        ? 'bg-surface-container text-primary font-semibold'
                        : 'text-on-surface hover:bg-surface-container-low'
                    }`}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                      {initialLetter}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm leading-tight">{opt.name}</div>
                      {opt.subText && (
                        <div className="truncate text-[11px] text-on-surface-variant">
                          {opt.subText}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <span className="material-symbols-outlined ml-1 shrink-0 text-sm text-primary">
                        check
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>,
          document.body
        )}
    </div>
  )
}

export default function CustomReportShareModal({ report, open, onClose, onSaved }) {
  const [shares, setShares] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [shareType, setShareType] = useState('ORG')
  const [canEdit, setCanEdit] = useState(false)
  const [canExport, setCanExport] = useState(true)
  const [canSchedule, setCanSchedule] = useState(false)
  const [userOptions, setUserOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])

  useEffect(() => {
    if (open && report?.id) {
      loadData()
    }
  }, [open, report?.id])

  async function loadData() {
    setLoading(true)
    try {
      const [rows, users, roles] = await Promise.all([
        fetchCustomReportShares(report.id),
        listUserOptions({ page_size: 200 }).catch(() => []),
        listRoleOptions({ page_size: 200 }).catch(() => []),
      ])
      setShares(rows || [])
      setUserOptions(Array.isArray(users) ? users : [])
      setRoleOptions(Array.isArray(roles) ? roles : [])
    } catch (err) {
      console.error('Failed to load report shares:', err)
    } finally {
      setLoading(false)
    }
  }

  // Pre-formatted option lists
  const formattedUserOptions = useMemo(() => {
    return userOptions.map((u) => ({
      id: u.id,
      name: u.name,
      subText: u.email || u.code || '',
      code: u.code || '',
    }))
  }, [userOptions])

  const formattedRoleOptions = useMemo(() => {
    return roleOptions.map((r) => ({
      id: r.id,
      name: r.name,
      subText: r.code || '',
      code: r.code || '',
    }))
  }, [roleOptions])

  // Async search handlers
  const handleSearchUsers = useCallback(async (q) => {
    try {
      const data = await listUserOptions({ search: q, page_size: 50 })
      return (Array.isArray(data) ? data : []).map((u) => ({
        id: u.id,
        name: u.name,
        subText: u.email || u.code || '',
        code: u.code || '',
      }))
    } catch {
      return []
    }
  }, [])

  const handleSearchRoles = useCallback(async (q) => {
    try {
      const data = await listRoleOptions({ search: q, page_size: 50 })
      return (Array.isArray(data) ? data : []).map((r) => ({
        id: r.id,
        name: r.name,
        subText: r.code || '',
        code: r.code || '',
      }))
    } catch {
      return []
    }
  }, [])

  function handleAddShare() {
    let defaultTargetName = ''
    if (shareType === 'ORG') defaultTargetName = 'Organization-Wide (All Employees)'
    else if (shareType === 'DEPARTMENT') defaultTargetName = 'My Department'
    else if (shareType === 'TEAM') defaultTargetName = 'My Project Team'
    else defaultTargetName = '' // Selected via search box in list element

    setShares((prev) => [
      ...prev,
      {
        shareType,
        targetId: null,
        targetName: defaultTargetName,
        canView: true,
        canEdit,
        canExport,
        canSchedule,
        canDuplicate: true,
      },
    ])
  }

  function updateShare(index, patch) {
    setShares((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }

  function removeShare(index) {
    setShares((prev) => prev.filter((_, i) => i !== index))
  }

  // Check if any USER or ROLE row is missing target selection
  const hasIncompleteShares = shares.some(
    (sh) => (sh.shareType === 'USER' || sh.shareType === 'ROLE') && !sh.targetId
  )

  async function handleSave() {
    if (hasIncompleteShares) return
    setSaving(true)
    try {
      await saveCustomReportShares(report.id, shares)
      onSaved?.()
      onClose()
    } catch (err) {
      console.error('Failed to save shares:', err)
    } finally {
      setSaving(false)
    }
  }

  if (!open || !report) return null

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm">
      <div className="flex flex-col w-full max-w-lg max-h-[85vh] overflow-hidden rounded-[14px] bg-surface p-4 shadow-2xl border border-surface-container space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-container pb-2.5">
          <div>
            <h3 className="text-sm font-bold text-on-surface">Share Report Permissions</h3>
            <p className="text-[11px] text-on-surface-variant truncate max-w-sm">{report.reportName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-on-surface rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>

        {/* Add Share Form */}
        <div className="p-3 bg-surface-container-low rounded-[10px] border border-surface-container space-y-2.5">
          <label className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant block">
            Grant Access To
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <AppDropdown
                value={shareType}
                onChange={setShareType}
                options={SHARE_TYPE_OPTIONS}
                triggerClassName="w-full h-9 text-xs px-3 rounded-[8px] border border-surface-container-high bg-surface flex items-center justify-between"
              />
            </div>
            <button
              type="button"
              onClick={handleAddShare}
              className="h-9 px-4 bg-primary text-on-primary rounded-[8px] text-xs font-bold shrink-0 shadow-xs flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              Add
            </button>
          </div>

          <div className="flex items-center justify-between text-xs pt-1 px-1 border-t border-surface-container/50">
            <span className="text-[10px] uppercase font-semibold text-on-surface-variant">Default Rights</span>
            <div className="flex items-center gap-3.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-on-surface">Export</span>
                <AppToggle checked={canExport} onChange={setCanExport} ariaLabel="Can Export" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-on-surface">Edit</span>
                <AppToggle checked={canEdit} onChange={setCanEdit} ariaLabel="Can Edit" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-on-surface">Schedule</span>
                <AppToggle checked={canSchedule} onChange={setCanSchedule} ariaLabel="Can Schedule" />
              </div>
            </div>
          </div>
        </div>

        {/* Existing Shares List */}
        <div className="flex-1 overflow-y-auto max-h-56 space-y-2 pr-0.5">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-xs text-on-surface-variant gap-2">
              <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              <span>Loading permissions...</span>
            </div>
          ) : shares.length === 0 ? (
            <div className="text-center py-6 px-4 bg-surface-container-lowest rounded-[10px] border border-dashed border-surface-container">
              <span className="material-symbols-outlined text-2xl text-on-surface-variant/50 mb-1 block">lock</span>
              <p className="text-xs text-on-surface font-semibold">This report is currently Private</p>
              <p className="text-[11px] text-on-surface-variant mt-0.5">
                Only you have access to view or edit this report. Use the selector above to grant access.
              </p>
            </div>
          ) : (
            shares.map((sh, idx) => (
              <div
                key={idx}
                className="p-2.5 bg-surface-container-low rounded-[10px] border border-surface-container space-y-2 text-xs"
              >
                {/* Header row: Type badge/label + Search Box + Remove button */}
                <div className="flex items-center gap-2">
                  {sh.shareType === 'USER' ? (
                    <>
                      <span className="text-[11px] font-bold text-primary shrink-0 w-10">User:</span>
                      <SearchSelect
                        value={sh.targetId}
                        selectedName={sh.targetName}
                        onChange={(id, name) => updateShare(idx, { targetId: id, targetName: name })}
                        onSearchAsync={handleSearchUsers}
                        initialOptions={formattedUserOptions}
                        placeholder="Search user by name..."
                        emptyMessage="No matching users found"
                        icon="person"
                      />
                    </>
                  ) : sh.shareType === 'ROLE' ? (
                    <>
                      <span className="text-[11px] font-bold text-amber-600 shrink-0 w-10">Role:</span>
                      <SearchSelect
                        value={sh.targetId}
                        selectedName={sh.targetName}
                        onChange={(id, name) => updateShare(idx, { targetId: id, targetName: name })}
                        onSearchAsync={handleSearchRoles}
                        initialOptions={formattedRoleOptions}
                        placeholder="Search role by name..."
                        emptyMessage="No matching roles found"
                        icon="badge"
                      />
                    </>
                  ) : (
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="material-symbols-outlined text-base text-primary shrink-0">
                        {sh.shareType === 'ORG'
                          ? 'domain'
                          : sh.shareType === 'DEPARTMENT'
                          ? 'corporate_fare'
                          : 'groups'}
                      </span>
                      <span className="font-bold text-on-surface text-xs truncate">
                        {sh.targetName ||
                          (sh.shareType === 'ORG'
                            ? 'Organization-Wide (All Employees)'
                            : sh.shareType === 'DEPARTMENT'
                            ? 'My Department'
                            : 'My Project Team')}
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => removeShare(idx)}
                    className="text-slate-400 hover:text-error p-1 rounded-full shrink-0 transition-colors"
                    title="Remove permission"
                  >
                    <span className="material-symbols-outlined text-sm">delete</span>
                  </button>
                </div>

                {/* Permissions row for this specific share item */}
                <div className="flex items-center justify-between text-[11px] text-on-surface-variant pt-1.5 border-t border-surface-container/60">
                  <div className="flex items-center gap-1 font-semibold text-emerald-600">
                    <span className="material-symbols-outlined text-xs">visibility</span>
                    <span>View</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <AppToggle
                        checked={sh.canExport}
                        onChange={(val) => updateShare(idx, { canExport: val })}
                        ariaLabel="Can Export"
                      />
                      <span>Export</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <AppToggle
                        checked={sh.canEdit}
                        onChange={(val) => updateShare(idx, { canEdit: val })}
                        ariaLabel="Can Edit"
                      />
                      <span>Edit</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <AppToggle
                        checked={sh.canSchedule}
                        onChange={(val) => updateShare(idx, { canSchedule: val })}
                        ariaLabel="Can Schedule"
                      />
                      <span>Schedule</span>
                    </label>
                  </div>
                </div>

                {/* Validation warning if unassigned */}
                {(sh.shareType === 'USER' || sh.shareType === 'ROLE') && !sh.targetId && (
                  <p className="text-[10px] text-amber-600 font-medium">
                    * Please search and select a {sh.shareType === 'USER' ? 'user' : 'role'} above
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2.5 border-t border-surface-container">
          <div>
            {hasIncompleteShares && (
              <span className="text-[10px] text-amber-600 font-medium">
                Select a user/role for all entries to save
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-[8px] text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || hasIncompleteShares}
              onClick={handleSave}
              className="px-4 py-1.5 bg-primary text-on-primary rounded-[8px] text-xs font-bold shadow-xs hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
